/**
 * `enrichment` procedure group — design §3.2.
 *
 * THREE THINGS THIS SURFACE DELIBERATELY DOES:
 *
 *  1. **`run` returns a COST ESTIMATE and refuses to spend without `confirm`.**
 *     Design §3.2 gates every paid run on "explicit opt-in and a shown cost
 *     estimate"; a procedure that could spend a credit on a bare call would make
 *     that a UI convention rather than a rule. The unconfirmed call is not an
 *     error — it is how the UI GETS the estimate to show.
 *  2. **Candidates, never contact writes.** `run` returns what it proposed;
 *     `candidates.accept` is what turns a proposal into a field, through the
 *     merge layer.
 *  3. **`findPhone` is NOT here.** Mobile lookup is 8-10 credits against 1 and
 *     the design makes it an explicit costed button; Wave 1 does not expose it
 *     on the API at all, so it cannot be reached by accident.
 */

import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.ts';
import {
  candidateStatusSchema,
  emailVerificationStatusSchema,
  enrichmentProviderSchema,
  enrichmentTriggerSchema,
  mergeActionSchema,
} from './enums.ts';

export const fieldCandidateSchema = z.object({
  id: uuidSchema,
  contactId: uuidSchema,
  /** `title` | `emails[]` | `linkedin_url` | ... */
  fieldPath: z.string(),
  /** The proposed value, as stored. Always a string in this release. */
  value: z.unknown(),
  provider: enrichmentProviderSchema,
  confidence: z.number().min(0).max(1).nullable(),
  verificationStatus: emailVerificationStatusSchema.nullable(),
  status: candidateStatusSchema,
  /** Why the merge layer parked it. Rendered verbatim in the review queue. */
  reason: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  appliedAt: isoDateTimeSchema.nullable(),
});

/** What a run WOULD cost, in vendor credits, before anything is spent. */
export const enrichmentEstimateSchema = z.object({
  /** Ordered exactly as the waterfall would run them. */
  stages: z.array(
    z.object({
      stage: z.string(),
      provider: enrichmentProviderSchema.nullable(),
      credits: z.int().min(0),
      /** Why this stage would (or would not) run for THIS contact. */
      note: z.string(),
    }),
  ),
  totalCredits: z.int().min(0),
  /** False when a cap would refuse the run; the UI shows the reason. */
  affordable: z.boolean(),
  blockedReason: z.string().nullable(),
});

export const enrichmentRunInputSchema = z.object({
  contactId: uuidSchema,
  /**
   * FALSE (the default) is a DRY RUN: it returns the estimate and spends
   * nothing. The UI calls it once to render the cost, then again with
   * `confirm: true` when the user taps.
   */
  confirm: z.boolean().default(false),
  /** Stage 0 only — recompute from free signals, never touch a vendor. */
  freeOnly: z.boolean().default(false),
  trigger: enrichmentTriggerSchema.default('user'),
});

export const enrichmentRunOutputSchema = z.object({
  contactId: uuidSchema,
  /** False on a dry run: nothing was queued and nothing was spent. */
  started: z.boolean(),
  estimate: enrichmentEstimateSchema,
  /** Present once the run is queued. */
  runId: uuidSchema.nullable(),
  /** Set when the provider answers asynchronously (FullEnrich). */
  pending: z.boolean(),
  /** Immediate Stage-0 proposals, when there were any. */
  candidates: z.array(fieldCandidateSchema),
});

export const enrichmentCandidatesListInputSchema = z.object({
  contactId: uuidSchema.optional(),
  status: candidateStatusSchema.optional(),
  limit: z.int().min(1).max(200).default(50),
});

export const enrichmentCandidatesListOutputSchema = z.object({
  items: z.array(fieldCandidateSchema),
});

export const enrichmentCandidateDecisionInputSchema = z.object({
  candidateId: uuidSchema,
});

export const enrichmentCandidateRejectInputSchema = z.object({
  candidateId: uuidSchema,
  /** Stored so the queue stops re-proposing the same wrong value. */
  reason: z.string().max(500).optional(),
});

export const enrichmentCandidateDecisionOutputSchema = z.object({
  candidate: fieldCandidateSchema,
  /** What the merge layer did with it, and why. */
  action: mergeActionSchema,
  reason: z.string(),
  applied: z.boolean(),
});

export const enrichmentRecordSchema = z.object({
  id: uuidSchema,
  contactId: uuidSchema.nullable(),
  provider: enrichmentProviderSchema,
  operation: z.string(),
  status: z.string(),
  creditsConsumed: z.int().min(0),
  triggeredBy: z.string(),
  startedAt: isoDateTimeSchema,
  completedAt: isoDateTimeSchema.nullable(),
  /**
   * When an async provider's webhook landed. Null with a completed status means
   * the result came from a poll; null with a running status past a few minutes
   * means the webhook never arrived.
   */
  webhookAt: isoDateTimeSchema.nullable(),
});

export const enrichmentHistoryInputSchema = z.object({
  contactId: uuidSchema.optional(),
  limit: z.int().min(1).max(200).default(50),
});

export const enrichmentHistoryOutputSchema = z.object({
  items: z.array(enrichmentRecordSchema),
});

export const enrichmentBudgetInputSchema = z.object({
  /** `YYYY-MM-01`. Defaults to the current month. */
  periodMonth: z
    .string()
    .regex(/^\d{4}-\d{2}-01$/)
    .optional(),
});

export const enrichmentBudgetOutputSchema = z.object({
  periodMonth: z.string(),
  providers: z.array(
    z.object({
      provider: enrichmentProviderSchema,
      creditsUsed: z.int().min(0),
      creditsCap: z.int().min(0),
      /** True once the cap would refuse the next reservation. */
      exhausted: z.boolean(),
      configured: z.boolean(),
    }),
  ),
});

/**
 * Design §3.2 STAGE 1, the first-name-only case: "<=20 ranked candidates ...
 * NEVER auto-accept. Surface top 3 in the UI — the user MET this person."
 */
export const enrichmentIdentifyInputSchema = z.object({
  contactId: uuidSchema.optional(),
  firstName: z.string().max(120).optional(),
  lastName: z.string().max(120).optional(),
  companyName: z.string().max(200).optional(),
  email: z.string().max(320).optional(),
  linkedinUrl: z.string().max(500).optional(),
  confirm: z.boolean().default(false),
});

export const identityCandidateSchema = z.object({
  matchScore: z.number(),
  fullName: z.string().nullable(),
  title: z.string().nullable(),
  companyName: z.string().nullable(),
  linkedinUrl: z.string().nullable(),
  location: z.string().nullable(),
  externalId: z.string().nullable(),
});

/**
 * The user picked one of the identify candidates — "this is them". The chosen
 * fields become `contact_field_candidates` (provider `pdl`) and are accepted
 * through the SAME merge layer as review-queue decisions, so provenance is
 * recorded and a value the user typed is never overwritten. This is the
 * counterpart of `identify`'s "never auto-accepted": the PICK is the human
 * decision, and this call is that decision arriving.
 */
export const enrichmentApplyIdentityInputSchema = z.object({
  contactId: uuidSchema,
  candidate: identityCandidateSchema,
});

export const enrichmentApplyIdentityOutputSchema = z.object({
  contactId: uuidSchema,
  /** One row per field the pick proposed, with what the merge layer did. */
  results: z.array(
    z.object({
      fieldPath: z.string(),
      /** The merge actions plus `skip` for a field that was decided earlier. */
      action: z.enum(['auto_apply', 'pending', 'reject', 'skip']),
      reason: z.string(),
      applied: z.boolean(),
    }),
  ),
});

export const enrichmentIdentifyOutputSchema = z.object({
  /** Empty on a dry run; the estimate still comes back. */
  candidates: z.array(identityCandidateSchema),
  estimate: enrichmentEstimateSchema,
  started: z.boolean(),
  /** Always false: identity candidates are a human decision, by design. */
  autoAccepted: z.literal(false),
});

export type FieldCandidateDto = z.output<typeof fieldCandidateSchema>;
export type EnrichmentEstimate = z.output<typeof enrichmentEstimateSchema>;
export type EnrichmentRunOutput = z.output<typeof enrichmentRunOutputSchema>;
